import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useMainOverlay } from '@/components/layout/main-overlay';
import { cn } from '@/lib/utils';

interface SideDrawerProps {
  /** Accessible name of the drawer; shown in its header when `title` is absent. */
  label: string;
  title?: ReactNode;
  /** Controls on the right of the header, before the close button. */
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}

// Drawers can open from inside a drawer (a table in an object's result): Escape closes the top one only.
const openDrawers: object[] = [];

/**
 * A panel sliding in from the right over the main content, the page
 * staying visible on its left. Closes with its button, the backdrop or Escape,
 * and gives focus back to what had it.
 */
export const SideDrawer = ({ label, title, actions, onClose, children }: SideDrawerProps) => {
  const host = useMainOverlay();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const token = {};
    openDrawers.push(token);
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    // Content may already have taken the focus (a fiche focuses itself for its keys).
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || openDrawers.at(-1) !== token) return;
      event.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      openDrawers.splice(openDrawers.indexOf(token), 1);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);

  const drawer = (
    <div className={cn('pointer-events-auto inset-0 z-50', host ? 'absolute' : 'fixed')}>
      {/* A click beside the drawer closes it; the page stays as it is, untinted. */}
      <div aria-hidden onClick={onClose} className="absolute inset-0" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-domino-cardBorder bg-domino-ivory shadow-2xl focus:outline-none sm:w-[min(48rem,88%)] motion-safe:animate-drawerIn motion-reduce:animate-drawerFade">
        <header className="flex min-h-14 shrink-0 items-center gap-3 border-b border-domino-cardBorder bg-domino-warm px-4 py-2 sm:px-6">
          <div className="min-w-0 flex-1 truncate font-display text-lg font-semibold tracking-tight text-domino-ink">{title}</div>
          {actions}
          <button type="button" onClick={onClose} aria-label="Fermer"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
            <X className="h-4 w-4" />
          </button>
        </header>
        {/* A size container: views inside follow the drawer's width (cq-*), the page's portal being elsewhere. */}
        <div className="view-container min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
  return createPortal(drawer, host ?? document.body);
};
